import { Button, Icon, MenuDivider, Tooltip } from "@blueprintjs/core"
import { useFeature } from "flagged"
import { partition } from "lodash-es"
import React, { useState } from "react"
import { useDispatch } from "react-redux"
import { graphql, useFragment } from "react-relay"

import styles from "./ViewsDropdown.module.css"

import { ViewsDropdown_user$key } from "~/Views/Planner/__generated__/ViewsDropdown_user.graphql"

import {
  getViewId,
  isDatabaseView,
  isViewEqual,
  isVirtualView,
} from "~/Views/helpers"
import { track } from "~/helpers/analytics"
import { reportError } from "~/helpers/error-helpers"

import { Dropdown, DropdownHeading } from "~/common/Dropdown"
import MenuItem from "~/common/MenuItem"
import PaidFeatureMenuItemContainer from "~/common/PaidFeatureMenuItemContainer"
import TooltipEllipsis from "~/common/TooltipEllipsis"
import { SimpleLabel } from "~/common/labels"
import { CrossIcon } from "~/common/react-icons"

import { userViewUpdate } from "~/mutations/User"

import { AvailableOnPlansChipUI } from "~/Entitlements/AvailableOnPlansChip"
import {
  ChargebeeFeatures,
  getPlanNameListForFeature,
} from "~/Entitlements/plansAndFeatures"
import {
  useEntitlementSwitch,
  useIsInTrial,
} from "~/Entitlements/useEntitlements"
import { usePermissions } from "~/Permissions/usePermissions"
import { setTemporaryPlannerFilter } from "~/Planner/reducer2/temporaryFilterSlice"
import { AvailableView, ViewID } from "~/Views/ViewsProvider"
import { useViews } from "~/Views/useViews"

import ViewFormDialog from "../ViewFormDialog"

const fragment = graphql`
  fragment ViewsDropdown_user on users
  @argumentDefinitions(
    projectsFilter: { type: "projects_bool_exp" }
    peopleFilter: { type: "people_bool_exp" }
  ) {
    id
    ...ViewFormDialog_user
      @arguments(peopleFilter: $peopleFilter, projectsFilter: $projectsFilter)
  }
`

type Props = {
  user: ViewsDropdown_user$key
  hideEdit?: boolean
  viewId?: ViewID
  onChange?: (view: AvailableView) => void
}

const ViewsDropdown = (props: Props) => {
  const { hideEdit = false, viewId } = props

  const user = useFragment(fragment, props.user)
  const {
    availableViews,
    databaseViews,
    virtualViews,
    selectedView: defaultSelectedView,
  } = useViews()
  const [searchValue, setSearchValue] = useState("")
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [viewToShow, setViewToShow] = useState(null)

  const entitledToViews = useEntitlementSwitch(ChargebeeFeatures.accountViews)
  const hasMyViews = useFeature("my_views")
  const isTrial = useIsInTrial()

  const { can, subject } = usePermissions()
  const canCreate = can("create", subject("View"))
  const canEdit = can("edit", subject("View"))
  const dispatch = useDispatch()
  const selectedView = viewId
    ? availableViews.find((v) => getViewId(v) === viewId)
    : defaultSelectedView

  if (!entitledToViews) {
    return null
  }

  if (!selectedView) {
    return null
  }

  if (databaseViews.length === 0 && virtualViews.length === 0) {
    return null
  }

  const filteredViews = availableViews.filter((view) =>
    view.name.toLowerCase().includes(searchValue.trim().toLowerCase()),
  )
  const filteredDatabaseViews = filteredViews.filter(isDatabaseView)
  const filteredVirtualViews = filteredViews.filter(isVirtualView)
  const [topViews, bottomViews] = partition(
    filteredVirtualViews,
    (view) => view.alias === "MY_VIEW",
  )
  const handleViewChange = async (nextView: AvailableView) => {
    if (props.onChange) {
      props.onChange(nextView)
      return
    }

    try {
      await userViewUpdate(nextView)
      dispatch(setTemporaryPlannerFilter(nextView))
      track("View Changed", {
        type: isDatabaseView(nextView) ? "Database" : "Virtual",
        value: getViewId(nextView),
      })
    } catch (error) {
      void reportError("Update user default view error: ", error)
    }
  }

  const Target = () => {
    return (
      <div
        className={styles.viewButton}
        data-component="ViewsDropdown/selectedView"
      >
        <TooltipEllipsis
          className={styles.selectedViewName}
          text={selectedView.name}
          placement="top"
        />
        <Button minimal icon="chevron-down" className={styles.chevronButton} />
      </div>
    )
  }

  const paidFeaturesSectionTitle = "Ready to start using Views?"
  const paidFeaturesSectionSubtitle =
    "Views are part of our Professional, Premium, and Enterprise plans"
  const planNamesForTooltip = getPlanNameListForFeature({
    featureId: ChargebeeFeatures.accountViews,
  })
  const myViewsMessage = `My View is available on ${planNamesForTooltip} plans`
  const myViewsDisabled = !hasMyViews

  const renderMenuItem = (
    view: AvailableView,
    options: {
      hideEdit: boolean
      canEdit: boolean
      isActive: boolean
      disabled?: boolean
      showPaidFeatureChip?: boolean
      showBetaLabel?: boolean
    },
  ) => {
    const hideDetailsButton =
      // Hide when "edit" is globally disabled (UI context specific)
      options.hideEdit ||
      // Hide for "all" view
      (isVirtualView(view) && view.alias === "ALL") ||
      // Hide for all but the currently selected view
      !options.isActive

    const text = (
      <div className={styles.menuItemText}>
        <div className={styles.nameAndBadges}>
          <p>{view.name}</p>
          {(options.showPaidFeatureChip || options.showBetaLabel) && (
            <div className={styles.badges}>
              {options.showPaidFeatureChip && (
                <AvailableOnPlansChipUI
                  message={myViewsMessage}
                  usePortal={true}
                />
              )}
              {options.showBetaLabel && (
                <SimpleLabel
                  text="Beta"
                  type="beta"
                  style={{ marginLeft: "5px" }}
                />
              )}
            </div>
          )}
        </div>
        {hideDetailsButton || (
          <button
            type="button"
            className={styles.defaultViewButton}
            onClick={() => setViewToShow(view)}
          >
            {/* Only show "Edit View" for database views */}
            {options.canEdit && !isVirtualView(view)
              ? "Edit View"
              : "View Details"}
          </button>
        )}
      </div>
    )
    const item = (
      <MenuItem
        key={getViewId(view)}
        text={text}
        softDisabled={!!options.disabled}
        data-component="ViewsDropdown/option"
        onClick={() => !options.disabled && handleViewChange(view)}
        active={options.isActive}
      />
    )
    return view.description ? (
      <Tooltip
        key={getViewId(view)}
        placement="right"
        content={view.description}
        openOnTargetFocus={false}
        disabled={!view.description}
        targetTagName="div"
      >
        {item}
      </Tooltip>
    ) : (
      item
    )
  }

  return (
    <>
      <Dropdown
        className={styles.dropdownContainer}
        Target={Target}
        minWidth={250}
        maxWidth={800}
        data-component="ViewsDropdown"
        preventRenderWhenClosed={true}
      >
        <div className={styles.searchInputContainer}>
          {searchValue ? (
            <div
              className={styles.clearButton}
              onClick={() => setSearchValue("")}
            >
              <CrossIcon />
            </div>
          ) : (
            <Icon icon="search" color="var(--shadow)" />
          )}
          <input
            type="text"
            placeholder="Search"
            className={styles.searchInput}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
        <MenuDivider />

        {Boolean(filteredViews.length) ? (
          <>
            <div className={styles.dropdownListContainer}>
              <DropdownHeading>Views</DropdownHeading>
              {topViews.map((view) => (
                <React.Fragment key={getViewId(view) || `top-${view.name}`}>
                  {renderMenuItem(view, {
                    canEdit,
                    isActive: isViewEqual(view, selectedView),
                    showPaidFeatureChip:
                      isVirtualView(view) &&
                      view.alias === "MY_VIEW" &&
                      isTrial,
                    showBetaLabel:
                      isVirtualView(view) && view.alias === "MY_VIEW",
                    hideEdit: true,
                    disabled: myViewsDisabled,
                  })}
                </React.Fragment>
              ))}
              {topViews.length > 0 && <MenuDivider />}
              {filteredDatabaseViews
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((view) => (
                  <React.Fragment key={getViewId(view) || `db-${view.name}`}>
                    {renderMenuItem(view, {
                      hideEdit,
                      canEdit,
                      isActive: isViewEqual(view, selectedView),
                    })}
                  </React.Fragment>
                ))}
              <MenuDivider />
              {bottomViews.map((view) => (
                <React.Fragment key={getViewId(view) || `bottom-${view.name}`}>
                  {renderMenuItem(view, {
                    hideEdit,
                    canEdit,
                    isActive: isViewEqual(view, selectedView),
                  })}
                </React.Fragment>
              ))}
            </div>
          </>
        ) : (
          <p className={styles.noResultsText}>No views found</p>
        )}

        <MenuDivider />
        {entitledToViews && !hideEdit && canEdit && (
          <MenuItem
            text="Create New View"
            onClick={() => setShowCreateForm(true)}
            className={styles.createButton}
          />
        )}
        {!entitledToViews && (
          <PaidFeatureMenuItemContainer
            roundedButton
            title={paidFeaturesSectionTitle}
            subtitle={paidFeaturesSectionSubtitle}
          />
        )}
      </Dropdown>
      {viewToShow && (
        <ViewFormDialog
          onClose={() => setViewToShow(null)}
          user={user}
          view={viewToShow}
          readonly={!canCreate}
        />
      )}
      {showCreateForm && (
        <ViewFormDialog
          onClose={() => setShowCreateForm(false)}
          user={user}
          readonly={!canEdit}
        />
      )}
    </>
  )
}

export default ViewsDropdown
